import {
  AIManager,
  AIManagerProvider,
  type CreateAiRequest,
} from '@carboncopyco/editor-ui';
import { getDecodedUrlToDisplay } from '@carboncopyco/editor-utils';
import {
  type GroupSchemaType,
  type IAIPromptGroup,
  IFindAndReplaceRuleType,
  type ITextReplaceRule,
  type Modification,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { memo } from 'react';
import {
  useEditor,
  useEditorAiPromptGroup,
  useEditorFindAndReplaceRules,
  useEditorMetadata,
} from '../context/EditorSdkProvider';
import { useEditorTool } from '../hooks/useEditorTool';

interface AIToolProps {
  innerHtml: string;
  getImage?: () => Promise<string>;
  onGetInnerHtml: () => void;
}

export const AITool = memo<AIToolProps>(
  ({ getImage, innerHtml, onGetInnerHtml }: AIToolProps) => {
    const editorSdk = useEditor();
    const { location } = useEditorMetadata();
    const pageUrl = getDecodedUrlToDisplay(location?.decodedUrl) ?? '';
    const { isOpen, close } = useEditorTool('aiManager');
    const aiPromptGroup = useEditorAiPromptGroup();
    const { rules } = useEditorFindAndReplaceRules();
    const textRules = rules.filter(
      (rule) => rule.type === IFindAndReplaceRuleType.replaceText
    );
    const createAiRequest = async ({
      name,
      prompt,
      dataList,
      modification,
    }: CreateAiRequest) => {
      // Create AI Prompt Modification
      let pages = [pageUrl];
      if (modification) {
        const isCurrentPageModification =
          modification?.content.pages.some((page) => page === pageUrl) ?? false;
        pages = isCurrentPageModification
          ? modification.content.pages
          : [...modification.content.pages, pageUrl];
      }
      try {
        await editorSdk.showBackdropOverlay(true);
        const res = (await editorSdk.createAiPromptModification({
          name,
          prompt,
          pages: pages,
          modification,
        })) as Modification<GroupSchemaType.aiPromptGroup>;

        // Extract ID
        const id = (res?.content as IAIPromptGroup)?.id;

        // Map over dataList and return promises
        const promises = dataList.map((data) => {
          const textReplaceRule = {
            oldValue: data.original,
            newValue: data.replace_with,
            options: {
              isCaseSensitive: false,
              isExactWord: true,
              applyOnUrls: ['*'],
            },
          } as ITextReplaceRule;

          // Return the promise
          return editorSdk.handleCreateTextReplaceRule({
            ...textReplaceRule,
            groupId: id,
          });
        });

        // Await all promises
        await Promise.all(promises);
        await editorSdk.reloadEditor();
      } catch (error) {
        await editorSdk.showBackdropOverlay(false);
        console.error('Error adding AI rule', error);
        notify.error('Error adding AI rule');
      }
    };

    const checkIfTheReplaceTextRuleExists = (text: string) => {
      return rules.some((rule) => {
        return (
          rule.type === IFindAndReplaceRuleType.replaceText &&
          rule.oldValue === text
        );
      });
    };

    return (
      <AIManagerProvider
        checkIfTheReplaceTextRuleExists={checkIfTheReplaceTextRuleExists}
        pageUrl={pageUrl}
        createAiRequest={createAiRequest}
        onAiRequest={editorSdk.aiAssist}
        innerHtml={innerHtml}
        getInnerHtml={onGetInnerHtml}
        getAllSubGroupModifications={editorSdk.getAllSubGroupModifications}
        textReplaceRules={textRules as ITextReplaceRule[]}
        getImage={getImage}
      >
        <AIManager
          isOpen={isOpen}
          onClose={close}
          pageUrl={pageUrl}
          modifications={aiPromptGroup}
          deleteModification={editorSdk.deleteAiPromptModification}
        />
      </AIManagerProvider>
    );
  }
);

AITool.displayName = 'AITool';
